export const DRIVER_STATUS = {
  INVITED: 'invited',
  TOKEN_SENT: 'token_sent',
  PENDING_APPLICATION: 'pending_application',
  APPLICATION_SUBMITTED: 'application_submitted',
  DOCUMENTS_PENDING: 'documents_pending',
  DOCUMENTS_UNDER_REVIEW: 'documents_under_review',
  DOCUMENTS_REJECTED: 'documents_rejected',
  DOCUMENTS_APPROVED: 'documents_approved',
  INSURANCE_PENDING: 'insurance_pending',
  EMBLEM_PENDING: 'emblem_pending',
  INSURANCE_UNDER_REVIEW: 'insurance_under_review',
  INSURANCE_QUOTE_SENT: 'insurance_quote_sent',
  INSURANCE_APPROVED: 'insurance_approved',
  INSURANCE_DECLINED: 'insurance_declined',
  CLOSED_NO_INSURANCE: 'closed_no_insurance',
  CLOSED_AGENT_TIMEOUT: 'closed_agent_timeout',
  GENERATING_DOCS: 'generating_docs',
  PENDING_REVIEW: 'pending_review',
  AWAITING_COUNTERSIGNATURE: 'awaiting_countersignature',
  SIGNATURE_PENDING: 'signature_pending',
  SIGNATURE_DRIVER_COMPLETED: 'signature_driver_completed',
  SIGNATURE_COMPLETED: 'signature_completed',
  COMPILING_FILE: 'compiling_file',
  APPROVED_FOR_DISPATCH: 'approved_for_dispatch',
  REJECTED: 'rejected',
  INACTIVE: 'inactive',
} as const

export type DriverStatus = (typeof DRIVER_STATUS)[keyof typeof DRIVER_STATUS]

// Statuses where the office must act next. `documents_approved + needs_insurance`
// (admin must submit to Emblem) can't be expressed by status alone and is
// deliberately excluded.
export const DRIVER_ACTION_NEEDED_STATUSES = [
  DRIVER_STATUS.APPLICATION_SUBMITTED,
  DRIVER_STATUS.DOCUMENTS_UNDER_REVIEW,
  DRIVER_STATUS.INSURANCE_UNDER_REVIEW,
  DRIVER_STATUS.PENDING_REVIEW,
  DRIVER_STATUS.AWAITING_COUNTERSIGNATURE,
  DRIVER_STATUS.SIGNATURE_DRIVER_COMPLETED,
] as const

// The driver statuses in which an Emblem decision still applies. A driver sent
// to Emblem twice keeps a second `submitted` row after the first is decided and
// the driver has moved on (closed, quoted, approved): that row is quoted by
// nobody, and acting on it would reopen a closed driver.
export const DRIVER_AWAITING_EMBLEM_STATUSES: readonly string[] = [
  DRIVER_STATUS.EMBLEM_PENDING,
  DRIVER_STATUS.INSURANCE_UNDER_REVIEW,
]

export const DRIVER_NEEDS_REVIEW_FILTER = 'needs_review' as const

// Driver statuses that mean insurance placement has cleared — the canonical
// "ready to approve into fleet" gate for the standard-driver / owner-operator
// flow. Lives here rather than in the backend because the broker's approve
// button mirrors it to decide whether to enable itself; a hand-kept frontend
// copy is how the button started lying about what the server would accept.
//
// Includes `approved_for_dispatch` on purpose: the compile pipeline
// (insurance/index.ts) can promote a driver to that status directly, outside
// the application-approve path, while the application row itself is still
// `submitted` or earlier. Without it the office had no way to close the
// application — approve rejected the driver as "not ready" even though the
// driver was already dispatchable. `onStandardDriverApproved` is idempotent on
// this state: `promoteToOperationalDriver` returns the existing row and
// `transferOnboardingDocumentsToFleet` skips docs matched on `sourceDocumentId`.
export const INSURANCE_CLEARED_DRIVER_STATUSES = [
  DRIVER_STATUS.INSURANCE_APPROVED,
  DRIVER_STATUS.GENERATING_DOCS,
  DRIVER_STATUS.AWAITING_COUNTERSIGNATURE,
  DRIVER_STATUS.SIGNATURE_PENDING,
  DRIVER_STATUS.SIGNATURE_DRIVER_COMPLETED,
  DRIVER_STATUS.SIGNATURE_COMPLETED,
  DRIVER_STATUS.COMPILING_FILE,
  DRIVER_STATUS.APPROVED_FOR_DISPATCH,
] as const

// Every status from the packet on: insurance cleared, plus `pending_review`,
// where the packet exists and the office owes a send, and where a driver waits
// while the agreement is signed on paper (`INS-61`).
export const POST_INSURANCE_DRIVER_STATUSES: readonly string[] = [
  ...INSURANCE_CLEARED_DRIVER_STATUSES,
  DRIVER_STATUS.PENDING_REVIEW,
]

export const INSURANCE_PATH = {
  HAS_OWN: 'has_own',
  NEEDS_INSURANCE: 'needs_insurance',
} as const

export type InsurancePath = (typeof INSURANCE_PATH)[keyof typeof INSURANCE_PATH]
